import React from 'react';

import { Button } from '../base/Button';
import { Modal } from '../../layouts/Modal';
import { TextField } from '../base/TextField';
import { Form } from '../base/Form/Form';
import { IFaultCause } from '../../types/Fault/FaultCause';
import useSuperAdminFaultCauseMutations from '../../data/fault/superadmin/useSuperAdminFaultCauseMutations';
import { TextArea } from '../base/TextArea';
import { on } from 'events';
import { redirect } from 'react-router';

export type FaultCauseModalMode = 'create' | 'edit' | 'clone';

interface CreateEditFaultCauseProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  faultCause?: IFaultCause;
  mode?: FaultCauseModalMode;
  onClone?: (createdCause: IFaultCause) => void;
}

interface FaultCauseFormFields {
  title: string;
  description: string;
}

export default function CreateEditFaultCause({
  open,
  onClose,
  faultCause,
  onClone,
  mode = faultCause ? 'edit' : 'create',
}: CreateEditFaultCauseProps) {
  const { createFaultCause, updateFaultCause } =
    useSuperAdminFaultCauseMutations();

  const handleClose = () => onClose(false);

  const handleCreate = (data: FaultCauseFormFields) =>
    createFaultCause.mutate(data, {
      onSuccess: (res) => {
        onClose(true);
        if (
          mode === 'clone' &&
          onClone &&
          res.data &&
          res.data.data &&
          res.data.data.length > 0
        ) {
          onClone(res.data.data[0]);
        }
      },
    });

  const handleUpdate = (data: FaultCauseFormFields) =>
    updateFaultCause.mutate(
      {
        ...faultCause!,
        ...data,
      },
      {
        onSuccess: () => onClose(true),
      }
    );

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create Fault Cause';
      case 'edit':
        return 'Edit Fault Cause';
      case 'clone':
        return 'Clone Fault Cause';
      default:
        return 'Fault Cause';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'create':
        return 'Use this form to create a new fault cause';
      case 'edit':
        return 'Use this form to edit the fault cause';
      case 'clone':
        return 'Use this form to create a copy of the selected fault cause';
      default:
        return 'Use this form to manage the fault cause';
    }
  };

  const getDefaultValues = () => {
    if (!faultCause) return {};

    if (mode === 'clone') {
      return {
        title: `${faultCause.title} (Copy)`,
        description: faultCause.description,
      };
    }

    return {
      title: faultCause.title,
      description: faultCause.description,
    };
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case 'create':
        return 'Create';
      case 'edit':
        return 'Update';
      case 'clone':
        return 'Clone';
      default:
        return 'Save';
    }
  };

  const handleSubmit = (data: FaultCauseFormFields) => {
    if (mode === 'create' || mode === 'clone') {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={getTitle()}
      description={getDescription()}
    >
      <Form<FaultCauseFormFields>
        defaultValues={getDefaultValues()}
        onSubmit={handleSubmit}
        className="mt-4 grid grid-cols-1 gap-y-4"
      >
        {({ register }) => (
          <>
            <TextField
              {...register('title', { required: true })}
              label="Title"
            />
            <TextArea
              {...register('description', { required: true })}
              label="Description"
            />
            <div className="mt-6 space-x-4">
              <Button type="submit">{getSubmitButtonText()}</Button>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
}
